//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { IMRBaseControlController } from '../../../../../common/base'

import { OperatorSecurityService } from '../../../../../../services/operator'
import { TypeHelper, LocalizationHelper, Dictionary } from '../../../../../../helpers'
import { UserSessionService } from '../../../../../../services/user-session'
import { SystemService } from '../../../../../../services/system'

import { Activity } from '../../../../../../models/common'
import { OpModule, OpOperator } from '../../../../../../models/core'
import { IReferenceType } from '../../../../../../models/util/interfaces'

import { IMRConfirmationPopupResult, IMRConfirmationPopupOptions, IIMRConfirmationPopup, IMRConfirmationPopupInitializedArgs } from '../../../../../common/confirmation-popup2'
import { IMRTabOptions } from '../../../../../common/tabs/tab'
import { IMRTabsOptions, IMRTabsState, IMRTabsTabSelectedArgs, IMRTabsTabSelectingArgs, IMRTabsInitializedArgs } from '../../../../../common/tabs'

import { OperatorDetailsSecurityEffectiveOptions } from './components/effective'
import { OperatorDetailsSecurityRolesOptions, OperatorDetailsSecurityRolesSubmitArgs, OperatorDetailsSecurityRolesInitializedArgs, IOperatorDetailsSecurityRoles } from './components/roles'
import { OperatorDetailsSecurityCustomOptions, IOperatorDetailsSecurityCustom, OperatorDetailsSecurityCustomInitializedArgs, OperatorDetailsSecurityCustomSubmitArgs } from './components/custom'
import { OperatorDetailsSecurityOptions, IOperatorDetailsSecurity } from './operator-details.security.interfaces'


export class OperatorDetailsSecurityController
    extends
    IMRBaseControlController<IOperatorDetailsSecurity, OperatorDetailsSecurityOptions>
    implements
    IOperatorDetailsSecurity {

    //#region Consts

    private get OPERATOR_DETAILS_SECURITY_EFFECTIVE_TAB_NAME(): string { return 'OPERATOR_DETAILS_SECURITY_EFFECTIVE_TAB_NAME' }
    private get OPERATOR_DETAILS_SECURITY_ROLES_TAB_NAME(): string { return 'OPERATOR_DETAILS_SECURITY_ROLES_TAB_NAME' }
    private get OPERATOR_DETAILS_SECURITY_CUSTOM_PERMISSIONS_TAB_NAME(): string { return 'OPERATOR_DETAILS_SECURITY_CUSTOM_PERMISSIONS_TAB_NAME' }

    //#endregion


    //#region Members

    public isLoading: boolean

    private activityReferenceDict: Dictionary<IReferenceType[]>
    private combinedModules: OpModule[]
    private operator: OpOperator

    //#endregion


    //#region Ctor

    public constructor(
        private systemService: SystemService,
        private userSessionService: UserSessionService,
        private operatorSecurityService: OperatorSecurityService
    ) {
        super()
    }

    //#endregion


    //#region Component

    protected initialize(options: OperatorDetailsSecurityOptions): JQueryPromise<void> {
        this.isLoading = true

        return $
            .when(
                this.initializeConfirmationPopup()
            )
            .then(() => {
                return $.when<any>(
                    this.operatorSecurityService.getCombinedModules(),
                    this.refresh(options.operator)
                )
            })
            .then((combinedModules: OpModule[]) => {
                this.combinedModules = combinedModules

                return this.initializeSecurityTabs()
            })
            .then(() => {
                this.isLoading = false
            })
    }

    private onOperatorSaved(savedOperator: OpOperator) {
        super.callEvent(this.options.onOperatorSaved, {
            operator: savedOperator
        })

        return this.refresh(savedOperator)
    }

    private refresh(operator: OpOperator) {
        this.operator = operator

        return this.operatorSecurityService
            .getActivityReferenceTypeDict(this.operatorSecurityService.filterCustomActivity(operator.allActivities))
            .then((activityReferenceDict: Dictionary<IReferenceType[]>) => {
                this.activityReferenceDict = activityReferenceDict
            })
    }

    //#endregion


    //#region IOperatorDetailsSecurity Implementation

    public hasChanges(): boolean {
        const hasChangesList: boolean[] = [
            this.securityRoles != null ? this.securityRoles.hasChanges() : false,
            this.securityCustom != null ? this.securityCustom.hasChanges() : false,
        ]

        return _.some(hasChangesList, (hasChanges: boolean) => hasChanges)
    }


    //#endregion


    //#region Confirmation Popup

    public confirmationPopupOptions: IMRConfirmationPopupOptions
    private confirmationPopup: IIMRConfirmationPopup

    private initializeConfirmationPopup(): JQueryPromise<void> {
        this.confirmationPopupOptions = {
            content: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AreYouSure),
            title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.UnsavedChanges),

            onInitialized: (e: IMRConfirmationPopupInitializedArgs) => {
                this.confirmationPopup = e.component
            }
        }

        return TypeHelper.onInitializedAll(this.confirmationPopupOptions)
    }

    //#endregion


    //#region Tabs

    public securityTabsOptions: IMRTabsOptions
    public securityRolesTabOptions: IMRTabOptions
    public securityCustomTabOptions: IMRTabOptions
    public securityEffectiveTabOptions: IMRTabOptions

    private initializeSecurityTabs(): void {
        this.securityTabsOptions = {
            tabsCSSClass: 'operators-details__small-tabs',

            onInitialized: (e: IMRTabsInitializedArgs) => {
                this.initializeTab(e.component.currentTabName)
            },

            onStateStoring: (state: IMRTabsState) => {
                this.operatorSecurityService.saveTabState(state)
            },

            onStateRestoring: () => {
                return this.operatorSecurityService.restoreTabState()
            },

            onTabSelecting: (e: IMRTabsTabSelectingArgs) => {
                if (this.hasChanges()) {
                    e.cancelAsync = this.confirmationPopup
                        .show()
                        .then((result: IMRConfirmationPopupResult) => {
                            return result.canceled || result.declined
                        })
                }
            },

            onTabSelected: (e: IMRTabsTabSelectedArgs) => {
                this.initializeTab(e.currentTabName)
                this.clearTab(e.previousTabName)
            }
        }

        this.securityRolesTabOptions = {
            name: this.OPERATOR_DETAILS_SECURITY_ROLES_TAB_NAME,
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Roles),
            visible: this.userSessionService.getLoggedOperator().hasPermission(Activity.OPERATOR_PERMISSIONS_ROLE_CHOOSE)
        }

        this.securityCustomTabOptions = {
            name: this.OPERATOR_DETAILS_SECURITY_CUSTOM_PERMISSIONS_TAB_NAME,
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CustomPermissions),
            visible: this.userSessionService.getLoggedOperator().hasPermission(Activity.OPERATOR_PERMISSIONS_CUSTOM_PERM_CHOOSE)
        }

        this.securityEffectiveTabOptions = {
            name: this.OPERATOR_DETAILS_SECURITY_EFFECTIVE_TAB_NAME,
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.EffectivePermissions),
            visible: this.userSessionService.getLoggedOperator().hasPermission(Activity.OPERATOR_PERMISSIONS_EFFECTIVE_PERMISSIONS)
        }
    }

    private initializeTab(tabName: string): void {
        switch (tabName) {
            case this.OPERATOR_DETAILS_SECURITY_EFFECTIVE_TAB_NAME:
                this.initializeEffectivePermissions()
                break

            case this.OPERATOR_DETAILS_SECURITY_ROLES_TAB_NAME:
                this.initializeRoles()

                break

            case this.OPERATOR_DETAILS_SECURITY_CUSTOM_PERMISSIONS_TAB_NAME:
                this.initializeCustomPermissions()

                break
        }
    }

    private clearTab(tabName: string): void {
        switch (tabName) {
            case this.OPERATOR_DETAILS_SECURITY_EFFECTIVE_TAB_NAME:
                this.clearEffectivePermissions()

                break

            case this.OPERATOR_DETAILS_SECURITY_ROLES_TAB_NAME:
                this.clearRoles()

                break

            case this.OPERATOR_DETAILS_SECURITY_CUSTOM_PERMISSIONS_TAB_NAME:
                this.clearCustomPermissions()

                break
        }
    }

    //#endregion


    //#region Effective Permissions 

    public securityEffectiveOptions: OperatorDetailsSecurityEffectiveOptions

    private initializeEffectivePermissions() {
        this.securityEffectiveOptions = {
            activityReferenceDict: this.activityReferenceDict,
            operator: this.operator,
            modules: this.combinedModules,
        }
    }

    private clearEffectivePermissions(): void {
        this.securityEffectiveOptions = null
    }

    //#endregion


    //#region Roles

    public securityRolesOptions: OperatorDetailsSecurityRolesOptions
    private securityRoles: IOperatorDetailsSecurityRoles

    private initializeRoles(): void {
        this.securityRolesOptions = {
            activityReferenceDict: this.activityReferenceDict,
            operator: this.operator,
            modules: this.combinedModules,

            onInitialized: (e: OperatorDetailsSecurityRolesInitializedArgs) => {
                this.securityRoles = e.component
            },

            onDisposed: () => {
                this.securityRoles = null
            },

            onSubmit: (e: OperatorDetailsSecurityRolesSubmitArgs) => {
                this.systemService.showLoadIndicator()

                this.operatorSecurityService
                    .saveRolesToOperator(this.operator, e.addedRoles, e.deletedRoles)
                    .then((savedOperator: OpOperator) => {
                        return this.onOperatorSaved(savedOperator)
                    })
                    .then(() => {
                        e.component.setOperator(this.operator)
                        e.component.reset()
                    })
                    .always(() => {
                        this.systemService.hideLoadIndicator()
                    })
            }
        }
    }

    private clearRoles(): void {
        this.securityRoles = null
        this.securityRolesOptions = null
    }

    //#endregion


    //#region Custom Permissions

    public securityCustomOptions: OperatorDetailsSecurityCustomOptions
    private securityCustom: IOperatorDetailsSecurityCustom

    private initializeCustomPermissions(): void {
        this.securityCustomOptions = {
            activityReferenceDict: this.activityReferenceDict,
            operator: this.operator,
            modules: this.combinedModules,

            onInitialized: (e: OperatorDetailsSecurityCustomInitializedArgs) => {
                this.securityCustom = e.component
            },

            onDisposed: () => {
                this.securityCustom = null
            },

            onSubmit: (e: OperatorDetailsSecurityCustomSubmitArgs) => {
                this.systemService.showLoadIndicator()

                this.operatorSecurityService
                    .saveActivitiesToOperator(this.operator, e.addedActivities, e.modifiedActivities, e.deletedActivities)
                    .then((savedOperator: OpOperator) => {
                        return this.onOperatorSaved(savedOperator)
                    })
                    .then(() => {
                        e.component.setOperator(this.operator)
                        e.component.setActivityReferenceDict(this.activityReferenceDict)
                        
                        return e.component.reset()
                    })
                    .always(() => {
                        this.systemService.hideLoadIndicator()
                    })
            }
        }
    }

    private clearCustomPermissions() {
        this.securityCustomOptions = null
        this.securityCustom = null
    }

    //#endregion

}
